import React from 'react';
import { BookOpen, ImagePlus, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import {
  TREE_COVER_EMPTY_TAGLINE,
  TREE_COVER_PRIVACY_LINE,
  TREE_COVER_SPECS_LINE,
} from '../../utils/treeCoverUpload';

/**
 * Default heritage banner when a tree has no custom cover image.
 * Hero empty state adds archive name, guidance, and optional upload affordance.
 */
export function ArchiveCoverFallback({
  className,
  compact = false,
  treeName,
  showEmptyState = false,
  canUpload = false,
  onUploadClick,
  uploading = false,
}) {
  const showRichEmpty = showEmptyState && !compact;

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden bg-gradient-to-br from-[hsl(42_30%_96%)] via-[hsl(40_24%_94%)] to-[hsl(38_20%_90%)]',
        compact ? 'min-h-[88px]' : showRichEmpty ? 'min-h-[140px]' : 'min-h-[120px]',
        className
      )}
      aria-hidden={!showRichEmpty}
      data-testid="archive-cover-fallback"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, hsl(38 28% 88% / 0.9) 0%, transparent 45%), radial-gradient(circle at 80% 70%, hsl(42 22% 92% / 0.85) 0%, transparent 40%)',
        }}
      />

      {showRichEmpty ? (
        <div className="absolute inset-0 flex items-center justify-center px-4 py-6 sm:px-8">
          <div className="flex max-w-xl flex-col items-center text-center">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)]/70 text-[#8b7355]/55 shadow-[inset_0_1px_0_hsl(42_40%_100%/0.5)]">
              <BookOpen className="h-5 w-5" strokeWidth={1.25} aria-hidden />
            </div>
            {treeName ? (
              <p className="font-serif text-lg font-medium tracking-tight text-[hsl(24_12%_24%)] sm:text-xl">
                {treeName}
              </p>
            ) : null}
            <p className="mt-2 text-sm leading-relaxed text-[hsl(28_10%_38%)]">{TREE_COVER_EMPTY_TAGLINE}</p>
            <p className="mt-1.5 text-xs leading-relaxed text-[hsl(28_8%_46%)]">{TREE_COVER_SPECS_LINE}</p>
            <p className="mt-1 text-xs text-[hsl(28_8%_50%)]/90">{TREE_COVER_PRIVACY_LINE}</p>
            {canUpload ? (
              <Button
                type="button"
                size="sm"
                className="mt-4 rounded-full bg-[#1c1917] px-5 text-[#faf7f2] hover:bg-[#2c2824]"
                disabled={uploading}
                onClick={onUploadClick}
                data-testid="hero-cover-upload-btn"
              >
                {uploading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
                ) : (
                  <ImagePlus className="mr-2 h-4 w-4" aria-hidden />
                )}
                Upload cover
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={cn(
              'flex items-center justify-center rounded-2xl border border-[hsl(38_14%_86%)]/80 bg-[hsl(42_28%_99%)]/60 text-[#8b7355]/50 shadow-[inset_0_1px_0_hsl(42_40%_100%/0.5)]',
              compact ? 'h-10 w-10' : 'h-12 w-12'
            )}
          >
            <BookOpen className={cn(compact ? 'h-5 w-5' : 'h-6 w-6')} strokeWidth={1.25} />
          </div>
        </div>
      )}
    </div>
  );
}
